import React, {useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {setAuth} from './auth-slice';
import {messageState, setMessageShow} from '../message/messageSlice';
import {fetchProfileAsync, tenantState} from '../tenant/tenantSlice';
import {checkTokenAsync, logoutAsync, userState} from '../user/userSlice';
import {
  fetchUserUpgrades,
  upgradeState,
} from '../upgrade_products/upgradeProductsSlice';
import {fetchViewingsAsync} from '../viewing/viewing-slice';
import {fetchWithdrawReasonsAsync} from '../withdraw_reason/withdrawReasonSlice';
import {
  API_EMAIL,
  KEY_AUTHENTICATION_TOKEN,
  SLUG_LOGIN,
} from '../../utils/constants';
import {getCookie, isArray, isEmailAddress} from '../../utils/helpers';

function AuthFeature() {
  const dispatch = useDispatch();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const user = useSelector(userState);
  const {profile, pending} = useSelector(tenantState);
  const {lastFetchedUpgrades} = useSelector(upgradeState);
  const {flash} = useSelector(messageState);
  const authChecked = React.useRef(false);
  const lastPathname = React.useRef(pathname);
  const lastCheckedToken = React.useRef(null);

  const doLogout = useCallback(
    () =>
      dispatch(logoutAsync()).then(() => {
        // eslint-disable-next-line fp/no-mutating-methods
        navigate(SLUG_LOGIN);
      }),
    [dispatch, navigate]
  );

  useEffect(() => {
    // Check if user is Authenticated and logged-in
    dispatch(checkTokenAsync());
  }, [dispatch]);

  useEffect(() => {
    if (!user.pending && user.token) {
      const roles = !isArray(user.roles) ? [] : user.roles;
      const isTenant =
        roles.includes('Tenant') && isEmailAddress(user.username) === undefined;
      if (!isTenant) {
        doLogout();
      }
    }
  }, [user, doLogout]);

  useEffect(() => {
    // If token is not empty and user is logged-in, fetch profile data
    if (!!user.token && user.verified !== undefined) {
      const fullProfile = user.verified === true;
      dispatch(fetchProfileAsync(fullProfile));
    }
    return () => null;
  }, [user.token, user.verified, dispatch]);

  React.useEffect(() => {
    let promise;

    if (user.verified !== undefined && !lastFetchedUpgrades) {
      // eslint-disable-next-line fp/no-mutation
      promise = dispatch(fetchUserUpgrades());
      // eslint-disable-next-line no-console
      promise.unwrap().catch(console.error);
    }

    return () => {
      promise?.abort && promise.abort();
    };
  }, [user.verified, lastFetchedUpgrades, dispatch]);

  useEffect(() => {
    if (user.verified !== undefined && profile[API_EMAIL]) {
      dispatch(
        fetchViewingsAsync({
          [`filter[attendee_email]`]: profile[API_EMAIL],
        })
      );
    }
    if (user.verified === true && profile[API_EMAIL]) {
      dispatch(fetchWithdrawReasonsAsync());
    }
  }, [user.verified, profile, dispatch]);

  /**
   * Inline alerts belong to the page that raised them, so clear them on
   * navigation. Toasts (flash) are raised by the page being left behind — e.g.
   * account verification redirecting home — so they have to survive it.
   */
  useEffect(() => {
    if (lastPathname.current === pathname) {
      return;
    }
    lastPathname.current = pathname;
    if (!flash) {
      dispatch(setMessageShow(false));
    }
  }, [dispatch, flash, pathname]);

  useEffect(() => {
    const cookieToken = getCookie(KEY_AUTHENTICATION_TOKEN);
    if (
      !user.pending &&
      !!cookieToken &&
      !user.token &&
      cookieToken !== lastCheckedToken.current
    ) {
      // eslint-disable-next-line fp/no-mutation
      lastCheckedToken.current = cookieToken;
      dispatch(checkTokenAsync());
    }
  }, [dispatch, pathname, user]);

  useEffect(() => {
    const lastCookieToken = getCookie(KEY_AUTHENTICATION_TOKEN);
    const interval = setInterval(() => {
      const cookieToken = getCookie(KEY_AUTHENTICATION_TOKEN);
      if (
        !user.pending &&
        !!lastCookieToken &&
        lastCookieToken !== cookieToken
      ) {
        dispatch(checkTokenAsync());
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [dispatch, user.pending, user.token]);

  useEffect(() => {
    if (!user.pending) {
      // eslint-disable-next-line fp/no-mutation
      authChecked.current = true;
    }
    dispatch(
      setAuth({
        fetching: user.token ? pending : !authChecked.current,
        loggedIn: !!getCookie(KEY_AUTHENTICATION_TOKEN) || !!user.token,
        userVerified: user.verified,
      })
    );
  }, [dispatch, pending, user]);

  return null;
}

export default AuthFeature;
